/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import type { BadgeTone } from "../components/ui/Badge";
import { CARE_STAFF_ROLES, CLIENT_ROLES } from "../api/accounts";

const STATUS_TONES: Record<string, BadgeTone> = {
  ACTIVE: "success",
  DISABLED: "neutral",
  BLOCKED: "error",
  UNVERIFIED: "warning",
  PENDING_ACTIVATION: "warning",
  CONSENT_REQUIRED: "warning",
};

export function statusTone(status: string): BadgeTone {
  return STATUS_TONES[status] ?? "neutral";
}

/**
 * A person row minted at Healthie ingest for a patient whose family never registered:
 * PENDING_ACTIVATION with the machine-authored ehr-<id> username no human path produces.
 */
export function neverRegistered(status: string, username: string | null): boolean {
  return status === "PENDING_ACTIVATION" && username !== null && username.startsWith("ehr-");
}

export function statusLabel(status: string): string {
  const word = status.replaceAll("_", " ").toLowerCase();
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function fullName(
  firstName: string | null,
  lastName: string | null,
  fallback = "Unnamed account",
): string {
  const name = [firstName, lastName].filter(Boolean).join(" ");
  return name === "" ? fallback : name;
}

const whenFormat = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

export function formatWhen(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) {
    return "—";
  }
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : whenFormat.format(parsed);
}

/** A staff target holds any role outside the client roles; only an Admin edits or resets it. */
export function isStaffAccount(roles: string[]): boolean {
  return roles.some((role) => !(CLIENT_ROLES as readonly string[]).includes(role));
}

/** Admin, Support or CareCoordinator: the viewer runs families' care, care teams included. */
export function isCareStaff(hasRole: (role: string) => boolean): boolean {
  return CARE_STAFF_ROLES.some(hasRole);
}

/** Spaces out the wire-format role for display: StandaloneClient reads "Standalone client". */
export function roleLabel(role: string): string {
  const spaced = role.replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0) + spaced.slice(1).toLowerCase();
}
